/**
 * Whose work this device is showing.
 *
 * > *"you can not access the work from one account via logging in to it: say I got my work on my
 * > pc, on an email and an account, I can on that pc change log in and still keep the same like
 * > stuff, which is not good, new log in = another account = ONLY THEIR SAVED STUFF SHOWN"*
 *
 * Until now a workspace was a BROWSER. One IndexedDB called `euclid` per profile, and signing in
 * changed nothing about it — so two accounts on one machine saw one pile of sheets, and the same
 * account on two machines saw two different piles. Both halves of that are wrong and they are the
 * same fault: identity was never part of the address.
 *
 * ── The rule ───────────────────────────────────────────────────────────────────────────────
 *
 * One database per account, and the database is chosen by who is signed in:
 *
 * | Signed in as | Reads and writes |
 * |---|---|
 * | nobody, on a device whose work is unclaimed | `euclid` |
 * | nobody, after some account claimed this device | `euclid-guest` |
 * | the account that claimed this device | `euclid` |
 * | any other account | `euclid-u-<id>` |
 *
 * ── Why claiming, and not copying ──────────────────────────────────────────────────────────
 *
 * `euclid` holds work that predates accounts entirely — on the owner's MacBook it is months of
 * it. The safe way to give that work to an account is to say so in a ledger and leave every byte
 * where it is. Copying it into a new database means a write path that can half-finish, and a
 * delete afterwards that cannot be taken back. Nothing here moves or deletes a record, ever.
 *
 * Claiming is offered, never automatic: the first time an account signs in on a device holding
 * unclaimed work, `AdoptWork.vue` asks. Declining is remembered per account, so the question is
 * put once and then dropped.
 */
import { databaseName, useDatabase } from './storage';

/** Who was signed in last time, so the right database opens before the session is confirmed. */
const LAST_KEY = 'euclid.workspace.last';

/** Which account, if any, owns the original `euclid` database on this device. */
const OWNER_KEY = 'euclid.workspace.owner';
/** Accounts that were offered this device's unclaimed work and said no. */
const DECLINED_KEY = 'euclid.workspace.declined';

/** The database that has been on every device since before accounts existed. */
export const ORIGINAL = 'euclid';
/** Where a guest works once some account has claimed the original. */
export const GUEST = 'euclid-guest';

/** Who the app currently believes is signed in. Set by `enter`, read by the other stores. */
let active: string | null = null;

/** The account the scoped databases are currently opened for. */
export const activeAccount = (): string | null => active;

const read = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};

const write = (key: string, value: string): void => {
  try { localStorage.setItem(key, value); } catch { /* private window */ }
};

const readList = (key: string): string[] => {
  const raw = read(key);
  try {
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
};

/** The account id that owns the original database here, or `null` while it is unclaimed. */
export const claimedBy = (): string | null => read(OWNER_KEY);

/** Whether this account has already been asked about the work sitting in the original database. */
export const wasOffered = (accountId: string): boolean =>
  claimedBy() !== null || readList(DECLINED_KEY).includes(accountId);

/** Take the original database for this account. Moves no data — it writes down an owner. */
export function claim(accountId: string): void {
  if (claimedBy() !== null) return;
  write(OWNER_KEY, accountId);
}

/** Remember that this account was offered the device's work and chose to start clean. */
export function decline(accountId: string): void {
  write(DECLINED_KEY, JSON.stringify([...new Set([...readList(DECLINED_KEY), accountId])]));
}

/**
 * The database name for an account — or for nobody, which is a guest.
 *
 * A guest keeps the original only while it is unclaimed. Once an account owns it, a guest gets
 * `euclid-guest`, because "signed out" must not be a way to read somebody's work.
 */
export function workspaceFor(accountId: string | null): string {
  return scopedName(ORIGINAL, accountId);
}

/**
 * The same rule, applied to the other databases an identity owns.
 *
 * The work is not the only thing that belongs to an account. `euclid-profile` holds the display
 * name and the picture, and `euclid-teams` holds the teams this device has joined — and while
 * those stayed per-DEVICE, signing in with a second address showed you the first person's name,
 * their portrait and their teams. From the outside that is indistinguishable from not having
 * switched account at all, which is precisely what was asked to be fixed.
 *
 * `euclid-books` is deliberately NOT scoped: a shelf is a library, not work. Two people sharing a
 * laptop should not each have to add the same chapter, and a book carries nothing personal.
 */
export function scopedName(base: string, accountId: string | null): string {
  const owner = claimedBy();
  if (accountId === null) return owner === null ? base : `${base}-guest`;
  if (owner === accountId) return base;
  return `${base}-u-${accountId}`;
}

/** Every database that follows the signed-in account, so a caller can reopen them together. */
export const SCOPED = [ORIGINAL, 'euclid-profile', 'euclid-teams'] as const;

/**
 * An anonymous session is a device, not an account.
 *
 * The rest of the app already draws that line — the gate still asks the three questions to an
 * anonymous session — and a workspace has to draw it too, or every fresh browser would get a
 * private database keyed to an id nobody can ever sign into again.
 */
export const ownerId = (account: { id: string; anonymous: boolean } | null): string | null =>
  account && !account.anonymous ? account.id : null;

/**
 * Who was signed in when this device was last used.
 *
 * Confirming a session is a round trip, and the workspace has to be chosen before first paint or
 * the app boots showing the wrong person's sheets for a second. So the last answer is written
 * down and used as the opening guess; `enter` is called again with the real answer when the
 * session resolves, and moves the pointer only if the guess was wrong.
 */
export const lastActive = (): string | null => read(LAST_KEY);

export function rememberActive(accountId: string | null): void {
  try {
    if (accountId === null) localStorage.removeItem(LAST_KEY);
    else localStorage.setItem(LAST_KEY, accountId);
  } catch { /* private window */ }
}

/**
 * Point storage at the workspace this account should see.
 *
 * Returns whether the pointer actually moved, so a caller knows whether it has to re-read what
 * it was holding. Everything the app has in memory came out of the old database.
 */
export async function enter(accountId: string | null): Promise<boolean> {
  rememberActive(accountId);
  const was = active;
  active = accountId;
  const wanted = workspaceFor(accountId);
  const moved = wanted !== databaseName();
  if (moved) await useDatabase(wanted);
  // The work's own pointer may not have moved — the owner of this device reads `euclid` either
  // way — while the profile and the teams still have to be told, so both are reported.
  return moved || was !== accountId;
}

/**
 * How much work is sitting in the original database, unclaimed.
 *
 * Read without a version so the open never triggers an upgrade, and guarded on the store list
 * because a versionless open CREATES the database when it is absent — at version 1 with nothing
 * in it. That is the trap the built-in chapters fell into on WebKit: the row count is not "is
 * there a database", it is "is there anything in it".
 *
 * The handle is closed on every path. An open connection to a database this device is not using
 * blocks a version change on it, and on WebKit blocks writes outright.
 */
export async function unclaimedWork(): Promise<number> {
  if (claimedBy() !== null) return 0;
  let db: IDBDatabase | null = null;
  try {
    db = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(ORIGINAL);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error('blocked'));
    });

    const counted = ['sheets', 'projects', 'tasks'].filter(name => db!.objectStoreNames.contains(name));
    if (counted.length === 0) return 0;

    return await new Promise<number>((resolve, reject) => {
      const tx = db!.transaction(counted, 'readonly');
      let total = 0;
      for (const name of counted) {
        const request = tx.objectStore(name).count();
        request.onsuccess = () => { total += request.result; };
      }
      tx.oncomplete = () => resolve(total);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } catch {
    // Not being able to look is not evidence of work. Saying "no" here costs an offer that can be
    // made again next time; saying "yes" would put a dialog in front of somebody with nothing.
    return 0;
  } finally {
    try { db?.close(); } catch { /* already gone */ }
  }
}
